import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetCustomerQueryKey,
  useGetCustomer,
  useGrantDoorAccess,
  useRevokeDoorAccess,
} from '../../../api/generated/endpoints/customers/customers'
import type { CustomerBookingRead, DoorAccessRead } from '../../../api/generated/model'
import { apiMessage } from '../../../lib/api-error'
import { formatInstant } from '../../../lib/time'

/**
 * One person: what we know about them, and whether they can open the door.
 *
 * The door panel deliberately reports two separate things — what an admin
 * decided, and whether the lock has been told. They are allowed to disagree
 * for a while, and a screen that hid that would be lying about the only part
 * that matters when the gateway is down.
 */
export const Route = createFileRoute('/_authed/_admin/admin/customer/$userId')({
  component: CustomerDetailPage,
})

function CustomerDetailPage() {
  const { userId } = Route.useParams()
  const [revealed, setRevealed] = useState(false)
  const customer = useGetCustomer(userId, { reveal_pin: revealed })

  if (customer.isPending) return <p className="text-slate-600">Cargando…</p>
  if (customer.isError || !customer.data) {
    return (
      <p role="alert" className="text-red-700">
        {apiMessage(customer.error, 'No se pudo cargar el cliente.')}
      </p>
    )
  }

  const { customer: person, door_access, pin, bookings, notes } = customer.data

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/admin/customers"
          className="inline-flex min-h-12 items-center text-base text-slate-600 underline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Volver a clientes
        </Link>
        <h1 className="mt-2 text-3xl font-semibold">{person.name ?? 'Cliente sin nombre'}</h1>
      </div>

      <section className="grid gap-4 rounded-lg border-2 border-slate-300 bg-white p-6 sm:grid-cols-2">
        <Detail label="Teléfono" value={person.phone ?? 'Sin registrar'} />
        <Detail label="Correo" value={person.email ?? 'Sin registrar'} />
        <Detail label="Horas disponibles" value={`${person.credit_hours} h`} />
        <Detail
          label="Origen"
          value={
            person.created_via === 'desk'
              ? 'Alta en el mostrador'
              : person.created_via === 'admin_booking'
                ? 'Creado al reservar por mostrador'
                : person.created_via === 'magic_link'
                  ? 'Cuenta propia'
                  : 'Sin registrar'
          }
        />
        {notes && (
          <p className="whitespace-pre-line text-base text-slate-700 sm:col-span-2">{notes}</p>
        )}
      </section>

      <DoorPanel
        userId={userId}
        access={door_access}
        pin={pin}
        revealed={revealed}
        onReveal={() => setRevealed((was) => !was)}
      />

      <section className="space-y-3">
        <h2 className="text-2xl font-semibold">Reservas</h2>
        {bookings.length === 0 ? (
          <p className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 text-center text-slate-600">
            Todavía no reservó ninguna hora.
          </p>
        ) : (
          <ul className="space-y-2">
            {bookings.map((booking) => (
              <BookingRow key={booking.id} booking={booking} />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <p className="flex flex-col">
      <span className="text-base text-slate-600">{label}</span>
      <span className="text-xl">{value}</span>
    </p>
  )
}

function BookingRow({ booking }: { booking: CustomerBookingRead }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border-2 border-slate-200 bg-white px-5 py-3">
      <span className="text-lg tabular-nums">{formatInstant(booking.starts_at)}</span>
      <span className="text-base text-slate-700">{booking.bay}</span>
    </li>
  )
}

/**
 * The key to the building.
 *
 * `granted` is what an admin decided and is true the instant they decide it.
 * `in_sync` is whether the lock has been told. On a revocation those coming
 * apart is the dangerous case — somebody we locked out still has a working
 * code — so it is stated in words rather than left to a green tick.
 */
function DoorPanel({
  userId,
  access,
  pin,
  revealed,
  onReveal,
}: {
  userId: string
  access: DoorAccessRead
  pin: string | null | undefined
  revealed: boolean
  onReveal: () => void
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const refresh = () => queryClient.invalidateQueries({ queryKey: getGetCustomerQueryKey(userId) })

  const grant = useGrantDoorAccess({
    mutation: {
      onSuccess: refresh,
      onError: (err) => setError(apiMessage(err, 'No se pudo dar el acceso.')),
    },
  })
  const revoke = useRevokeDoorAccess({
    mutation: {
      onSuccess: refresh,
      onError: (err) => setError(apiMessage(err, 'No se pudo quitar el acceso.')),
    },
  })
  const busy = grant.isPending || revoke.isPending

  // No door configured yet. Showing the panel would offer a button that opens
  // nothing and then report a code that reveals nothing -- see
  // DoorAccessRead.enabled.
  if (!access.enabled) return null

  return (
    <section className="space-y-4 rounded-lg border-2 border-slate-300 bg-white p-6">
      <h2 className="text-2xl font-semibold">Acceso al local</h2>

      {!access.granted ? (
        <>
          <p className="text-base text-slate-700">
            Sin acceso. Al darlo se genera un código y se envía por correo.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setError(null)
              grant.mutate({ userId })
            }}
            className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            {grant.isPending ? 'Dando acceso…' : 'Dar acceso'}
          </button>
        </>
      ) : (
        <>
          <p className="text-base text-slate-700">
            Con acceso desde {access.granted_at ? formatInstant(access.granted_at) : 'siempre'}.
          </p>

          <DoorState access={access} />

          <div className="rounded-md bg-slate-100 px-5 py-4">
            <span className="text-base text-slate-600">Código</span>
            <p className="mt-1 text-3xl tabular-nums">
              {!revealed ? '••••••' : (pin ?? 'No se puede leer el código guardado')}
            </p>
            <button
              type="button"
              onClick={onReveal}
              className="mt-3 min-h-14 rounded-md border-2 border-slate-400 px-6 text-lg focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              {revealed ? 'Ocultar' : 'Mostrar código'}
            </button>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setError(null)
              revoke.mutate({ userId })
            }}
            className="min-h-16 rounded-md border-2 border-red-700 px-8 text-xl text-red-700 disabled:opacity-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            {revoke.isPending ? 'Quitando acceso…' : 'Quitar acceso'}
          </button>
        </>
      )}

      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
    </section>
  )
}

/** What the lock knows, as opposed to what we decided. */
function DoorState({ access }: { access: DoorAccessRead }) {
  if (access.code_status === 'abandoned') {
    return (
      <p className="rounded-md bg-amber-100 px-5 py-4 text-base text-amber-950">
        El código se generó tres veces y nunca se usó, así que dejó de reemplazarse. Conviene
        entregarlo en persona y probarlo en el teclado.
      </p>
    )
  }
  if (!access.in_sync) {
    return (
      <p className="rounded-md bg-amber-100 px-5 py-4 text-base text-amber-950">
        La cerradura todavía no confirmó el cambio. Se reintenta solo cada pocos minutos.
        {access.last_error && (
          <span className="mt-1 block font-mono text-sm">{access.last_error}</span>
        )}
      </p>
    )
  }
  if (!access.used) {
    const reissues = access.reissues ?? 0
    return (
      <p className="rounded-md bg-slate-100 px-5 py-4 text-base text-slate-700">
        Todavía no se usó. Conviene probarlo en la primera visita: un código sin usar se reemplaza
        automáticamente y llega uno nuevo por correo.
        {reissues > 0 && ` Ya se reemplazó ${reissues} ${reissues === 1 ? 'vez' : 'veces'}.`}
      </p>
    )
  }
  return <p className="text-base text-green-900">En uso. El código no va a cambiar.</p>
}
